/**
 * Signal orders drawer — a docked, READ-ONLY list of the shared `bot_all`
 * account's live running positions + pending orders. Toggled from the
 * right-rail signal button. Data comes from the `signal` slice (fed by the
 * signal SSE stream), never the user's own `trading` slice. No actions —
 * every row is display-only, matching the read-only nature of the reference.
 */
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setSignalOrdersOpen } from "@/state/slices/uiSlice";
import styles from "./SignalOrdersPanel.module.css";

const fmtPrice = (v: number) => v.toFixed(3);
const fmtVol = (v: number) => v.toFixed(2);
const fmtMoney = (v: number) =>
  `${v >= 0 ? "" : "-"}$${Math.abs(v).toFixed(2)}`;

export function SignalOrdersPanel() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.signalOrdersOpen);
  const enabled = useAppSelector((s) => s.signal.enabled);
  const online = useAppSelector((s) => s.signal.online);
  const symbol = useAppSelector((s) => s.signal.symbol);
  const account = useAppSelector((s) => s.signal.account);
  const positions = useAppSelector((s) => s.signal.positions);
  const orders = useAppSelector((s) => s.signal.orders);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setSignalOrdersOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  if (!open || !enabled) return null;

  const totalPnl = positions.reduce((s, p) => s + (p.profit ?? 0), 0);

  return (
    <aside className={styles.drawer} aria-label="Signal orders">
      <div className={styles.header}>
        <div className={styles.title}>
          <span
            className={styles.dot}
            style={{ background: online ? "#22c55e" : "#f59e0b" }}
            aria-hidden
          />
          Signal · {symbol}
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close signal orders"
          onClick={() => dispatch(setSignalOrdersOpen(false))}
        >
          ×
        </button>
      </div>

      {!online ? (
        <div className={styles.empty}>Source offline — bot_all not running.</div>
      ) : (
        <div className={styles.body}>
          {/* Account summary */}
          <div className={styles.summary}>
            <div className={styles.summaryCell}>
              <span className={styles.summaryLabel}>Equity</span>
              <span className={styles.summaryValue}>
                {account ? fmtMoney(account.equity) : "—"}
              </span>
            </div>
            <div className={styles.summaryCell}>
              <span className={styles.summaryLabel}>Floating P&L</span>
              <span
                className={styles.summaryValue}
                style={{ color: totalPnl >= 0 ? "#22c55e" : "#ef4444" }}
              >
                {fmtMoney(totalPnl)}
              </span>
            </div>
          </div>

          {/* Running positions */}
          <div className={styles.sectionHead}>
            Running positions <span className={styles.count}>{positions.length}</span>
          </div>
          {positions.length === 0 ? (
            <div className={styles.emptyRow}>No open positions.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Side</th>
                  <th className={styles.num}>Vol</th>
                  <th className={styles.num}>Entry</th>
                  <th className={styles.num}>Current</th>
                  <th className={styles.num}>P&L</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const isBuy = p.type === 0;
                  return (
                    <tr key={p.ticket}>
                      <td>
                        <span
                          className={styles.side}
                          style={{ color: isBuy ? "#22c55e" : "#ef4444" }}
                        >
                          {isBuy ? "BUY" : "SELL"}
                        </span>
                      </td>
                      <td className={styles.num}>{fmtVol(p.volume)}</td>
                      <td className={styles.num}>{fmtPrice(p.price_open)}</td>
                      <td className={styles.num}>{fmtPrice(p.price_current)}</td>
                      <td
                        className={styles.num}
                        style={{ color: p.profit >= 0 ? "#22c55e" : "#ef4444" }}
                      >
                        {fmtMoney(p.profit)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pending orders */}
          <div className={styles.sectionHead}>
            Pending orders <span className={styles.count}>{orders.length}</span>
          </div>
          {orders.length === 0 ? (
            <div className={styles.emptyRow}>No pending orders.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th className={styles.num}>Vol</th>
                  <th className={styles.num}>Price</th>
                  <th className={styles.num}>SL</th>
                  <th className={styles.num}>TP</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const isBuy = o.type_label.toLowerCase().startsWith("buy");
                  return (
                    <tr key={o.ticket}>
                      <td>
                        <span
                          className={styles.side}
                          style={{ color: isBuy ? "#22c55e" : "#ef4444" }}
                        >
                          {o.type_label.toUpperCase()}
                        </span>
                      </td>
                      <td className={styles.num}>{fmtVol(o.volume_current)}</td>
                      <td className={styles.num}>{fmtPrice(o.price_open)}</td>
                      <td className={styles.num}>{o.sl > 0 ? fmtPrice(o.sl) : "—"}</td>
                      <td className={styles.num}>{o.tp > 0 ? fmtPrice(o.tp) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </aside>
  );
}
